import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { Modal, Button, Form, Alert, Card, Spinner, Row, Col, Badge } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagic, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import {
  useUserComposablePredicates,
  useUserSectionColumns,
  useGenerateUserCustomAnalyticRecipe,
  useCreateUserCustomAnalytic,
  useUserCustomAnalytic,
  useUpdateUserCustomAnalytic
} from 'api/hooks/use-custom-analytics'
import RecipeBuilder from 'admin/custom-analytics/RecipeBuilder'
import RecipeBuilderContext from 'admin/custom-analytics/RecipeBuilderContext'
import { defaultRecipe, validateRecipe, ensureKeys, recipeForApi } from 'admin/custom-analytics/recipe-helpers'
import RecipePlainSummary from 'analytics/RecipePlainSummary'
import { useAnalyticPolicyRules } from 'api/hooks/use-analytic-policy-rules'

// Only quote policy rules the matcher linked with reasonable confidence —
// mirrors the threshold the analytic show page uses for its linked-rules list.
const POLICY_RULE_CONFIDENCE_THRESHOLD = 0.65

// Compose OR edit a custom analytic. Self-contained (reuses the shared
// RecipeBuilder) and wired to the municipality-user endpoints, since the
// surfaces that open it (policies page, analytic show page) are
// municipality-user surfaces. Pass `analyticId` to edit an existing analytic;
// omit it to compose a new one. On save it hands the analytic back via
// onSaved so the caller can link it to a rule / refresh / re-run.
//
// Compose mode is generate-first: as soon as the modal opens with rule text,
// the AI drafts the recipe AND its metadata (name, description template,
// admin-facing description). The user sees name/template plus a plain-text
// summary of the recipe; the predicate-tree editor stays behind a "Show
// recipe details" link, and the admin-facing description is saved but never
// shown here.
const ComposeAnalyticModal = ({
  show,
  onHide,
  municipalityId,
  analyticId = null,
  sections = [],
  defaultSectionId,
  seedDescription = '',
  saving = false,
  onSaved
}) => {
  const isEdit = !!analyticId
  const queryClient = useQueryClient()
  const [sectionId, setSectionId] = useState(defaultSectionId || sections[0]?.id || '')
  const [draft, setDraft] = useState(null)
  const [aiDescription, setAiDescription] = useState('')
  const [aiError, setAiError] = useState(null)
  const [formError, setFormError] = useState(null)
  const [showDetails, setShowDetails] = useState(false)
  // Edit mode keeps the AI panel collapsed behind a "Regenerate recipe"
  // button; compose mode shows it (pre-seeded with the rule text) up front.
  const [showRegenerate, setShowRegenerate] = useState(false)
  // Guards against a slow generate/save response landing after the modal
  // closed (or reopened for something else) and clobbering the new session.
  const sessionRef = useRef(0)
  // Auto-generate fires at most once per open.
  const autoGenFiredRef = useRef(false)
  const seededRef = useRef(null)

  const { data: registry } = useUserComposablePredicates({ municipalityId, options: { enabled: show && !!municipalityId } })
  const { data: editData, isLoading: editLoading } = useUserCustomAnalytic({
    municipalityId,
    id: analyticId,
    options: { enabled: show && isEdit }
  })
  const { data: columnsData, isLoading: columnsLoading } = useUserSectionColumns({
    municipalityId,
    sectionId,
    options: { enabled: show && !!municipalityId && !!sectionId }
  })
  const generateMutation = useGenerateUserCustomAnalyticRecipe({ municipalityId })
  const createMutation = useCreateUserCustomAnalytic({ municipalityId })
  const updateMutation = useUpdateUserCustomAnalytic({ municipalityId })
  // Policy rules this analytic is linked to — quoted in the regenerate panel
  // so the recipe can be adjusted against the actual policy language.
  const { data: policyRules } = useAnalyticPolicyRules({
    analyticId: show && isEdit ? analyticId : null
  })
  const linkedRules = (policyRules || []).filter((pra) => pra.confidence >= POLICY_RULE_CONFIDENCE_THRESHOLD)

  const runGenerate = (targetSectionId, description) => {
    setAiError(null)
    const session = sessionRef.current
    generateMutation.mutate(
      { section_id: targetSectionId, description },
      {
        onSuccess: (data) => {
          if (session !== sessionRef.current) return
          setDraft((prev) => ({
            ...prev,
            // Keep anything the user already typed; only fill blanks.
            name: prev.name || data.name || '',
            description: prev.description || data.description || description,
            description_template: prev.description_template || data.description_template || '',
            recipe: ensureKeys(data.recipe)
          }))
        },
        onError: (err) => {
          if (session !== sessionRef.current) return
          setAiError(err?.response?.data?.message || err?.response?.data?.error || err.message)
        }
      }
    )
  }

  // Every open/close invalidates any in-flight mutation callbacks.
  useEffect(() => {
    sessionRef.current += 1
  }, [show])

  // Seed the form each time the modal opens. For a new analytic we start from
  // an empty recipe + the rule text as the AI prompt, and kick off generation
  // immediately. For an edit we wait for the raw analytic to load, then seed
  // its section, recipe and metadata.
  //
  // `seededRef` keeps this to once per open — these deps churn on every parent
  // render, and re-seeding mid-edit re-keys the tree (remounting the input
  // being typed into) and collapses the recipe-details panel.
  useEffect(() => {
    if (!show) {
      autoGenFiredRef.current = false
      seededRef.current = null
      return
    }
    if (isEdit) {
      if (!editData) return
      if (seededRef.current === `edit:${analyticId}`) return
      seededRef.current = `edit:${analyticId}`
      setSectionId(editData.section_id)
      setDraft({
        name: editData.name || '',
        description: editData.description || '',
        description_template: editData.description_template || '',
        recipe: ensureKeys(editData.recipe || defaultRecipe())
      })
      setAiDescription('')
      setShowRegenerate(false)
      setShowDetails(false)
    } else {
      const seededSectionId = defaultSectionId || sections[0]?.id || ''
      // Keyed on the resolved section so a late-arriving section list still seeds once.
      if (seededRef.current === `compose:${seededSectionId}`) return
      seededRef.current = `compose:${seededSectionId}`
      setSectionId(seededSectionId)
      setDraft({ name: '', description: '', description_template: '', recipe: defaultRecipe() })
      setAiDescription(seedDescription)
      setShowDetails(false)
      if (!autoGenFiredRef.current && seedDescription.trim() && seededSectionId) {
        autoGenFiredRef.current = true
        runGenerate(seededSectionId, seedDescription)
      }
    }
    setAiError(null)
    setFormError(null)
  }, [show, isEdit, analyticId, editData, defaultSectionId, seedDescription, sections])

  const recipeBuilderContextValue = useMemo(
    () => ({
      columns: columnsData?.columns || [],
      isLoading: columnsLoading,
      auditColumns: columnsData?.audit_columns || null
    }),
    [columnsData, columnsLoading]
  )

  const handleGenerate = () => runGenerate(sectionId, aiDescription)

  const handleSaved = (analytic) => {
    // Refresh every surface that renders this analytic: its show page, the
    // analytics list, and any policy document that links it.
    queryClient.invalidateQueries(['analytic'])
    queryClient.invalidateQueries(['analytics'])
    queryClient.invalidateQueries(['policy-document'])
    if (isEdit) queryClient.invalidateQueries(['user-custom-analytic', municipalityId, analyticId])
    // The ConfigEditor on the analytic show page (and its "tunable params"
    // list) is keyed on ['analytic_config_keys', <analyticId>] and doesn't
    // otherwise know the recipe just changed — without this it keeps showing
    // pre-edit params until a hard reload. Prefer the id the server just gave
    // us (covers both the edit path and a brand-new analytic); fall back to
    // prefix-invalidating every config-keys entry if that's ever missing.
    const savedAnalyticId = analytic?.id ?? (isEdit ? analyticId : null)
    if (savedAnalyticId != null) {
      queryClient.invalidateQueries(['analytic_config_keys', savedAnalyticId])
    } else {
      queryClient.invalidateQueries(['analytic_config_keys'])
    }
    onSaved?.(analytic)
  }

  const handleSave = () => {
    setFormError(null)
    const errors = validateRecipe(draft.recipe, registry?.predicates)
    if (errors.length > 0) {
      setShowDetails(true)
      setFormError(`${errors.length} issue(s) — see warnings in the recipe details below.`)
      return
    }
    const payload = {
      section_id: sectionId,
      name: draft.name,
      description: draft.description,
      description_template: draft.description_template,
      // Client-only _key node ids must never reach the API.
      recipe: recipeForApi(draft.recipe, registry?.predicates, columnsData?.audit_columns)
    }
    const session = sessionRef.current
    const opts = {
      onSuccess: (analytic) => handleSaved(analytic),
      onError: (err) => {
        if (session !== sessionRef.current) return
        setFormError(err?.response?.data?.error || err?.response?.data?.message || err.message)
      }
    }
    if (isEdit) {
      updateMutation.mutate({ id: analyticId, ...payload }, opts)
    } else {
      createMutation.mutate(payload, opts)
    }
  }

  const busy = createMutation.isLoading || updateMutation.isLoading || saving
  const generating = generateMutation.isLoading
  const loadingEdit = isEdit && (editLoading || !draft)

  // Numeric params currently set on the recipe — the "configuration" shown
  // alongside the template so it can be written into the per-flag message.
  const tunableConfig = useMemo(() => {
    const out = []
    const walk = (node) => {
      if (!node || typeof node !== 'object') return
      if (node.type) {
        const schema = registry?.predicates?.[node.type]
        ;(schema?.params || []).forEach((p) => {
          const v = node[p.name]
          if (['number', 'integer'].includes(p.type) && v !== undefined && v !== null && v !== '') {
            out.push({ label: p.ui_label || p.name, value: v })
          }
        })
        return
      }
      const children = node.op === 'NOT' ? [node.predicate] : node.predicates || []
      children.filter(Boolean).forEach(walk)
    }
    walk(draft?.recipe)
    return out
  }, [draft?.recipe, registry])

  const insertColumnPlaceholder = (columnName) => {
    setDraft((prev) => ({
      ...prev,
      description_template: `${prev.description_template || ''}{${columnName}}`
    }))
  }

  // Shared by compose AND edit mode: name + description template (with
  // insertable table columns and the recipe's configuration), the
  // plain-English summary, and the predicate tree behind a toggle.
  const renderFields = () => (
    <>
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label className="small text-muted">Name</Form.Label>
            <Form.Control
              type="text"
              value={draft.name || ''}
              onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
              placeholder={generating ? 'Generating…' : 'e.g. Weekend purchases over $500'}
              disabled={generating}
            />
          </Form.Group>
        </Col>
      </Row>
      {/* draft.description (admin-facing) is filled by the AI and saved with
          the analytic, but intentionally not rendered on this user-facing
          surface — admins see and edit it in the admin recipe builder. */}
      <Form.Group className="mb-3">
        <Form.Label className="small text-muted">Description template</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={draft.description_template || ''}
          onChange={(e) => setDraft((prev) => ({ ...prev, description_template: e.target.value }))}
          placeholder={generating ? 'Generating…' : '{column_name} renders the row\'s value at evaluation time'}
          disabled={generating}
        />
        <Form.Text className="text-muted">
          Shown on each flagged transaction — {'{column}'} placeholders fill in the row's values.
        </Form.Text>
        {(columnsData?.columns || []).length > 0 && (
          <div className="mt-2">
            <div className="small text-muted mb-1">Table columns — click to insert:</div>
            <div className="d-flex flex-wrap gap-1" style={{ maxHeight: 88, overflowY: 'auto' }}>
              {(columnsData?.columns || []).map((c) => (
                <Badge
                  key={c.id}
                  bg="light"
                  text="primary"
                  role="button"
                  style={{ cursor: 'pointer' }}
                  onClick={() => !generating && insertColumnPlaceholder(c.database_column_name)}
                >
                  {c.database_column_name}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {tunableConfig.length > 0 && (
          <div className="small text-muted mt-2">
            Configuration:{' '}
            {tunableConfig.map((t, idx) => (
              <span key={`${t.label}-${idx}`}>
                {idx > 0 && ', '}
                {t.label} <code>{t.value}</code>
              </span>
            ))}
          </div>
        )}
      </Form.Group>

      <h6 className="mb-2">How this analytic works</h6>
      {generating ? (
        <div className="text-center py-4 text-muted">
          <Spinner animation="border" size="sm" className="me-2" />
          Generating recipe…
        </div>
      ) : (
        <RecipePlainSummary recipe={draft.recipe} predicates={registry?.predicates} />
      )}

      <Button
        variant="link"
        size="sm"
        className="px-0 mb-2"
        onClick={() => setShowDetails((v) => !v)}
        disabled={generating}
      >
        <FontAwesomeIcon icon={showDetails ? faChevronUp : faChevronDown} className="me-1" />
        {showDetails ? 'Hide recipe details' : 'Show recipe details'}
      </Button>

      {showDetails && !generating && (
        <RecipeBuilderContext.Provider value={recipeBuilderContextValue}>
          <RecipeBuilder value={draft} registry={registry} onChange={setDraft} hideMetadata hideJson />
        </RecipeBuilderContext.Provider>
      )}
    </>
  )

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Edit analytic recipe' : 'Compose analytic from policy rule'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {formError && <Alert variant="danger">{formError}</Alert>}

        {loadingEdit ? (
          <div className="text-center py-4 text-muted">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading recipe…
          </div>
        ) : (
          <>
            <Form.Group className="mb-3">
              <Form.Label className="small text-muted">Module</Form.Label>
              <Form.Select
                value={sectionId}
                onChange={(e) => setSectionId(e.target.value)}
                disabled={isEdit}
              >
                {sections.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} ({s.abbreviation})</option>
                ))}
              </Form.Select>
              {isEdit && (
                <Form.Text className="text-muted">Module can't change on an existing analytic.</Form.Text>
              )}
            </Form.Group>

            <Card bg="light" className="mb-3">
              {isEdit && (
                <Card.Header className="d-flex justify-content-between align-items-center py-2">
                  <span className="small text-muted">
                    <FontAwesomeIcon icon={faMagic} className="me-2 text-primary" />
                    Describe what this analytic should flag — regenerating replaces the recipe below.
                  </span>
                  <Button
                    variant="light"
                    size="sm"
                    className="text-primary flex-shrink-0 ms-2"
                    onClick={() => setShowRegenerate((v) => !v)}
                    disabled={generating}
                  >
                    <FontAwesomeIcon icon={showRegenerate ? faChevronUp : faMagic} className="me-1" />
                    {showRegenerate ? 'Hide' : 'Regenerate recipe'}
                  </Button>
                </Card.Header>
              )}
              {(!isEdit || showRegenerate) && (
                <Card.Body>
                  {!isEdit && (
                    <Form.Label className="d-flex align-items-center small text-muted mb-2">
                      <FontAwesomeIcon icon={faMagic} className="me-2 text-primary" />
                      The recipe below was drafted from this rule text — adjust it and regenerate if needed.
                    </Form.Label>
                  )}
                  {isEdit && linkedRules.length > 0 && (
                    <div className="mb-2">
                      {linkedRules.map((pra) => (
                        <blockquote
                          key={pra.id}
                          className="mb-2 ps-3 small text-muted"
                          style={{ borderLeft: '3px solid #dee2e6', fontStyle: 'italic' }}
                        >
                          {pra.policy_rule.content || pra.policy_rule.summary}
                        </blockquote>
                      ))}
                    </div>
                  )}
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={aiDescription}
                    onChange={(e) => setAiDescription(e.target.value)}
                    disabled={generating}
                    placeholder={isEdit ? 'e.g. also exclude transactions under $100' : undefined}
                  />
                  {aiError && <Alert variant="danger" className="mt-2 mb-0 py-2 small">{aiError}</Alert>}
                  <div className="mt-2 d-flex justify-content-end">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleGenerate}
                      disabled={!aiDescription.trim() || !sectionId || generating}
                    >
                      {generating ? (
                        <><Spinner animation="border" size="sm" className="me-1" /> Generating…</>
                      ) : (
                        <><FontAwesomeIcon icon={faMagic} className="me-1" /> Regenerate recipe</>
                      )}
                    </Button>
                  </div>
                </Card.Body>
              )}
            </Card>

            {draft && renderFields()}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" className="text-primary" onClick={onHide}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} disabled={busy || loadingEdit || generating || !sectionId}>
          {busy ? 'Saving…' : (isEdit ? 'Save recipe' : 'Save & link')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ComposeAnalyticModal
